<script>
   /***********************************************/
   /* Svelte component with main app container    */
   /***********************************************/

   import DataLoader from './DataLoader.svelte';
   import PCVCalculator from './PCVCalculator.svelte';
   import ModelCalculator from './ModelCalculator.svelte';
   import AppPlots from './AppPlots.svelte';

   let dataset = null;  // calibration set
   let pvset = null;    // computed PV-set
   let model = null;    // global PCA model
   let calres = null;   // calibration results
   let pvres = null;    // PV-set results

   // combine both results to one object
   $: res = {cal: calres, pv: pvres};

   // if model is null - reset dependent objects
   $: if (model === null) {
      pvset = null;
      pvres = null;
      calres = null;
   }

   // if dataset is null - reset dependent objects
   $: if (dataset === null) {
      model = null;
   }
</script>

<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="data-loader">
         <DataLoader bind:dataset={dataset} />
      </div>
      {#if dataset }
      <div class="model-calculator">
         <ModelCalculator {dataset} bind:model={model} bind:calres={calres} />
      </div>
      {/if}
      {#if dataset && model }
      <div class="pcv-calculator">
         <PCVCalculator {dataset} {model} bind:pvset={pvset} bind:pvres={pvres} />
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !pvset || !pvres}
         <div class="help-text">
         {#if dataset === null}
            <p><strong>Step 1.</strong></p>
            <p>
               Load your calibration set from a CSV file. The file should contain numeric values delimited
               by comma (with dot as decimal separator) or by semicolon (with comma as decimal separator). Rows correspond to observations or objects, columns — to variables. First row can contain variable names (header) and first column can contain labels for the objects/observations — just set the corresponding options after selecting
               the file.
            </p>
         {:else}
            {#if model === null}
            <p><strong>Step 2</strong></p>
            <p>
               Next step is to create a global PCA model for the training set. Use more components/latent variables (PCs)
               than you expect to be optimal. If dataset is small (e.g. number of rows is smaller than 20), then using too many components will limit the choice of cross-validation splits. Try to play with this option.
            </p>
            {/if}

            {#if model !== null && pvset === null}
            <p><strong>Step 3</strong></p>
            <p>
               Now set necessary parameters for Procrustes cross-validation method and click button to compute the PV-set. The parameters are the same as you use for cross-validation: type of splits and number of segments. It is recommended to use fewer number of segments (e.g. 3, 4 or 5). The more segment you use the more time it will take to compute the PV-set.
            </p>
            <p>
               If dataset has small number of rows and number of components in global model is large, it limits the choice of cross-validation splits (in some cases on leave-one-out option is left).
            </p>
            {/if}
         {/if}
         </div>
      {:else}
         {#key pvres}
         <AppPlots {model} {res} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0 1em 0 0.5em;
      padding: 0.25em 0.25em;
   }

   .app-controls > div {
      background: #e8e8e8;
      border-radius: 0.5rem;
   }

   .help-text {
      margin-top: 0.25rem;
   }

   .help-text > p {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(320px, 30%) 1fr;
      }
   }

</style>